<template>
    <v-dialog
        :value="dialogValue"
        persistent
        @input="dialogValue = $event"
        width="600px"
        :key="modalOpen ? modal : 'form'"
    >
        <template v-if="modalOpen">
            <component :is="modal">
                <v-card-actions>
                    <v-btn
                        text
                        @click="privacyPolicyConfirmed = false, modalOpen = false"
                    >
                        {{ $t('tracing.privacyPolicy.deny') }}
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        depressed
                        @click="privacyPolicyConfirmed = true, modalOpen = false"
                    >
                        {{ $t('tracing.privacyPolicy.accept') }}
                    </v-btn>
                </v-card-actions>
            </component>
        </template>
        <form
            v-else
            @submit.prevent="onSubmit"
        >
            <v-card>
                <v-card-title>
                    <div>
                        <h2>{{ title }}</h2>
                    </div>
                    <v-spacer />
                    <v-btn
                        @click="dialogValue = false"
                        :disabled="pending"
                        icon
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <template v-if="feedbackEnabled">
                    <v-card-text>
                        <div v-html="feedbackIntro"></div>
                    </v-card-text>
                    <v-card-text class="pt-0">
                        <v-form
                            ref="feedbackForm"
                            v-model="feedbackValid"
                        >
                            <v-textarea
                                v-model="feedback"
                                :rules="feedbackRules"
                                :label="$t('label')"
                                filled
                            />
                        </v-form>
                    </v-card-text>
                </template>
                <template v-if="tracingEnabled">
                    <v-card-title
                        v-if="feedbackEnabled"
                        class="pt-0"
                    > {{ $t('title.tracing') }}
                    </v-card-title>
                    <v-card-text>
                        <div v-html="tracingIntro"></div>
                        <v-form
                            ref="tracingForm"
                            v-model="tracingValid"
                        >
                            <v-checkbox
                                class="pt-0"
                                v-model="privacyPolicyConfirmed"
                                :rules="confirmationRules"
                            >
                                <template #label>
                                    <div>
                                        {{ $t('tracing.privacyPolicy.label1') }}&nbsp;
                                        <a
                                            href="#"
                                            @click.stop.prevent="open($event, $t('locale'))"
                                        >{{ $t('tracing.privacyPolicy.label2') }}</a>
                                        &nbsp;{{ $t('tracing.privacyPolicy.label3') }}
                                    </div>
                                </template>
                            </v-checkbox>
                            <v-tooltip
                                color="primary"
                                max-width="400"
                                top
                                :disabled="privacyPolicyConfirmed"
                            >
                                <template
                                    #activator="{ on, attrs }"
                                >
                                    <div
                                        v-on="on"
                                        v-bind="attrs"
                                    >
                                        <v-text-field
                                            :disabled="!privacyPolicyConfirmed"
                                            v-model="name"
                                            :label="$t('tracing.name.label')"
                                            :rules="nameRules"
                                            filled
                                            dense
                                        ></v-text-field>
                                        <v-text-field
                                            :disabled="!privacyPolicyConfirmed"
                                            v-model="email"
                                            :rules="emailRules"
                                            :label="$t('tracing.email.label')"
                                            filled
                                            dense
                                        ></v-text-field>
                                        <v-text-field
                                            :disabled="!privacyPolicyConfirmed"
                                            v-model="company"
                                            :label="$t('tracing.company.label')"
                                            filled
                                            dense
                                        ></v-text-field>
                                    </div>
                                </template>
                                <span>{{ $t('disabledTooltip') }}</span>
                            </v-tooltip>

                        </v-form>
                    </v-card-text>
                </template>
                <v-card-actions>
                    <v-btn
                        :disabled="pending"
                        @click="dialogValue = false"
                        color="primary"
                        text
                    >
                        {{ $t('close') }}
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        type="submit"
                        color="primary"
                        :disabled="disabled"
                        :loading="pending"
                        depressed
                    >
                        {{ $t('submit') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </form>
    </v-dialog>
</template>

<i18n>
{
    "de": {
        "locale": "de",
        "title": {
            "feedback": "Feedback",
            "tracing": "Rückverfolgung"
        },
        "submit": "Senden",
        "close": "Schließen",
        "label": "Feedback",
        "error": "Bitte geben Sie Ihr Feedback ein",
        "disabledTooltip": "Stimmen Sie zuerst der Datenschutzvereinbarung zu",
        "tracing": {
            "privacyPolicy": {
                "accept": "akzeptieren",
                "deny": "ablehnen",
                "label1": "Ich habe die",
                "label2": "Datenschutzvereinbarung",
                "label3": "gelesen und stimme der Rückverfolgung zu."
            },
            "name": {
                "label": "Name",
                "error": "Bitte geben Sie einen Namen mit maximal 64 Zeichen an"
            },
            "email": {
                "label": "E-Mail",
                "error": "Bitte geben Sie eine gültige E-Mail-Adresse an"
            },
            "company": {
                "label": "Unternehmen"
            }
        }
    },
    "en": {
        "locale": "en",
        "title": {
            "feedback": "Feedback",
            "tracing": "Tracing"
        },
        "submit": "Submit",
        "close": "Close",
        "label": "Feedback",
        "error": "Please enter your feedback",
        "disabledTooltip": "Agree to the data protection agreement first",
        "tracing": {
            "privacyPolicy": {
                "accept": "accept",
                "deny": "reject",
                "label1": "I have read the",
                "label2": "data protection agreement",
                "label3": "and agree to the tracing."
            },
            "name": {
                "label": "Name",
                "error": "Please enter a name with less than 64 characters"
            },
            "email": {
                "label": "Email",
                "error": "Please enter a valid email address"
            },
            "company": {
                "label": "Company"
            }
        }
    }
}
</i18n>

<script>
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import DataProtectionDE from '@/components/meta/de/data-protection.vue'
import DataProtectionEN from '@/components/meta/en/data-protection.vue'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

@Component({
    components: {
        'de-data-protection': DataProtectionDE,
        'en-data-protection': DataProtectionEN
    }
})
export default class FeedbackDialog extends Vue {
    @Prop({ type: Boolean, default: false }) value
    @Prop({ type: Boolean, default: () => false }) feedbackEnabled
    @Prop({ type: String, required: true }) feedbackIntro
    @Prop({ type: Boolean, default: () => false }) tracingEnabled
    @Prop({ type: String, required: true }) tracingIntro
    @Prop({ type: Object, required: true }) tracingData
    @Prop({ type: Boolean, default: () => false }) pending
    @Prop({ type: Function, required: true }) submit

    modal = null
    modalOpen = false

    open (evt, locale) {
        this.modal = `${locale}-data-protection`
        this.modalOpen = true
    }

    feedback = ''
    name = ''
    email = ''
    company = ''
    privacyPolicyConfirmed = false
    dirty = false

    feedbackValid = false
    tracingValid = false

    get feedbackRules () {
        return [feedback => this.tracingEnabled || !!feedback || this.$t('error')]
    }

    get emailRules () {
        return [
            email => !!email || this.$t('tracing.email.error'),
            email => emailRegex.test(email) || this.$t('tracing.email.error')
        ]
    }

    get nameRules () {
        return [
            (name) => !!name || this.$t('tracing.name.error'),
            (name) => (name && name.length <= 64) || this.$t('tracing.name.error')
        ]
    }

    get confirmationRules () {
        return [confirmed => !!confirmed]
    }

    @Watch('privacyPolicyConfirmed')
    onPrivacyPolicyConfirmedChanged () {
        if (!this.privacyPolicyConfirmed) {
            this.$refs.tracingForm && this.$refs.tracingForm.reset()
        }
    }

    get dialogValue () {
        return this.value
    }

    set dialogValue (val) {
        this.$emit('input', val)
    }

    get title () {
        if (this.feedbackEnabled) {
            return this.$t('title.feedback')
        }
        if (this.tracingEnabled) {
            return this.$t('title.tracing')
        }
    }

    get disabled () {
        if (this.pending) {
            return true
        }

        // if privacy policy has already been submit, it must be possible to submit without confirmation and feedback
        if (this.tracingData.privacyPolicyConfirmed && !this.privacyPolicyConfirmed) {
            return false
        }

        if (this.feedbackEnabled && !this.tracingEnabled) {
            return !this.feedbackValid
        }

        if (this.tracingEnabled && !this.feedbackEnabled) {
            return !this.tracingValid
        }

        if (this.tracingEnabled && this.feedbackEnabled) {
            const neitherFeedbackNorTracingValid = !this.tracingValid && this.feedback === ''
            const policyConfirmedButTracingDataInvalid = !this.tracingValid && this.privacyPolicyConfirmed

            return neitherFeedbackNorTracingValid || policyConfirmedButTracingDataInvalid
        }

        return false
    }

    async onSubmit () {
        const tracingData = {
            name: this.tracingValid ? this.name || '' : '',
            email: this.tracingValid ? this.email || '' : '',
            company: this.tracingValid ? this.company || '' : '',
            privacyPolicyConfirmed: this.privacyPolicyConfirmed || false
        }

        await this.submit({
            feedback: this.feedback,
            ...tracingData
        })
    }

    created () {
        this.feedback = this.tracingData.feedback || ''
        this.name = this.tracingData.name || ''
        this.email = this.tracingData.email || ''
        this.company = this.tracingData.company || ''
        this.privacyPolicyConfirmed = this.tracingData.privacyPolicyConfirmed || false
    }
}
</script>
